import { apiClient } from './apiClient';

export interface Usher {
  id: string;
  first_name: string;
  last_name: string;
  phone: string;
  email?: string;
  is_approved: boolean;
  is_active: boolean;
  role: 'usher';
}

export interface UsherInvitation {
  id: string;
  usher: Usher;
  event: string;
  invitation_token: string;
  status: 'pending' | 'accepted' | 'expired' | 'revoked';
  invited_by: string;
  created_at: string;
  expires_at?: string;
  accepted_at?: string;
  // Additional fields from the API response
  usher_name?: string;
  usher_phone?: string;
  usher_email?: string;
  event_title?: string;
  invited_by_name?: string;
}

export interface CreateUsherInvitationData {
  event: string;
  usher: string;
  expires_at?: string;
}

export interface CreateUsherData {
  usher_first_name: string;
  usher_last_name: string;
  usher_phone?: string;
  usher_email?: string;
  expires_at?: string;
}

export interface UpdateUsherInvitationData {
  status?: 'pending' | 'accepted' | 'expired' | 'revoked';
}

class UsherAPI {
  // Get all ushers
  async getUshers(params?: {
    search?: string;
  }) {
    const response = await apiClient.get('/ushers/', { params });
    return response.data;
  }

  // Get usher invitations
  async getInvitations(params?: {
    event?: string;
    usher?: string;
    status?: string;
    page?: number;
    page_size?: number;
    _t?: number; // Timestamp for cache busting
  }, bypassCache: boolean = false) {
    // Always disable cache when event parameter is provided to ensure event-specific data
    const config: any = { 
      params,
      cache: bypassCache || params?.event ? false : true // Disable cache if bypassing or filtering by event
    };
    const response = await apiClient.get('/ushers/invitations/', config);
    return response.data;
  }

  // Create usher invitation
  async createInvitation(data: CreateUsherInvitationData) {
    const response = await apiClient.post('/ushers/invitations/', data);
    return response.data;
  }

  // Create usher directly (like guest creation)
  async createUsher(eventId: string, data: CreateUsherData) {
    const response = await apiClient.post(`/events/${eventId}/ushers/create/`, data);
    // Invalidate invitations cache after creating a usher
    apiClient.invalidateCache('ushers/invitations');
    return response.data;
  }

  // Update usher invitation
  async updateInvitation(invitationId: string, data: UpdateUsherInvitationData) {
    const response = await apiClient.patch(`/ushers/invitations/${invitationId}/`, data);
    return response.data;
  }

  // Delete usher invitation
  async deleteInvitation(invitationId: string) {
    const response = await apiClient.delete(`/ushers/invitations/${invitationId}/`);
    return response.data;
  }

  // Accept invitation (for ushers)
  async acceptInvitation(token: string) {
    const response = await apiClient.post(`/ushers/invitations/accept/${token}/`);
    return response.data;
  }

  // Get usher's assigned events
  async getUsherEvents() {
    const response = await apiClient.get('/ushers/events/');
    return response.data;
  }

  // Get guests for a specific event (usher view)
  async getEventGuests(eventId: string, searchQuery?: string) {
    const params = searchQuery ? { search: searchQuery } : {};
    const response = await apiClient.get(`/ushers/events/${eventId}/guests/`, { params });
    return response.data;
  }

  // Search for guests in an event
  async searchGuest(eventId: string, searchQuery: string) {
    const response = await apiClient.get(`/ushers/events/${eventId}/guests/`, {
      params: { search: searchQuery }
    });
    return response.data;
  }

  // Update guest attendance
  async updateAttendance(guestEventId: number, data: { attended: boolean }) {
    const response = await apiClient.patch(`/ushers/attendance/${guestEventId}/`, data);
    return response.data;
  }
}

export const usherAPI = new UsherAPI();
